import React from 'react';
import {
  Box, Button, Card, CardContent, Chip, FormControl, Grid, IconButton,
  InputLabel, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Typography
} from '@mui/material';
import {
  AccountBalance, Add, CheckCircle, Delete, DeleteOutlined, Edit, EditOutlined,
  HourglassEmpty, Pending, Payment, PaymentOutlined, Visibility, VisibilityOutlined
} from '@mui/icons-material';
import DateRangePickerField from '../components/DateRangePickerField';

export default function GeneralExpensesTab({
  generalExpenses = [],
  generalStats = { total_amount: 0, paid_amount: 0, pending_amount: 0 },
  categories = [],
  startDate,
  endDate,
  filterCategory,
  filterStatus,
  generalExpensePage,
  generalExpenseRowsPerPage,
  generalExpensesPagination = {},
  isMobile,
  formatCurrency,
  onStartDateChange,
  onEndDateChange,
  onCategoryChange,
  onStatusChange,
  onPageChange,
  onRowsPerPageChange,
  onAdd,
  onEdit,
  onDelete,
  onPayment,
  onOpenPaymentHistory,
  onMarkPaid,
}) {
  return (
    <>
      {/* Stats Cards */}
      <Grid container spacing={{ xs: 1.5, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
        <Grid item xs={4} md={4}>
          <Card sx={{
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
            boxShadow: '0 8px 32px rgba(249, 115, 22, 0.3)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(249, 115, 22, 0.4)' }
          }}>
            <CardContent sx={{ p: { xs: 1.5, md: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                    Σύνολο Εξόδων
                  </Typography>
                  <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ fontWeight: 700, color: '#fff', my: 0.5 }}>
                    {formatCurrency(generalStats.total_amount)}
                  </Typography>
                </Box>
                {!isMobile && <AccountBalance sx={{ fontSize: 48, color: 'rgba(255,255,255,0.25)' }} />}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4} md={4}>
          <Card sx={{
            borderRadius: 3,
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            boxShadow: '0 8px 32px rgba(34, 197, 94, 0.3)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(34, 197, 94, 0.4)' }
          }}>
            <CardContent sx={{ p: { xs: 1.5, md: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                    Πληρωμένα
                  </Typography>
                  <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ fontWeight: 700, color: '#fff', my: 0.5 }}>
                    {formatCurrency(generalStats.paid_amount)}
                  </Typography>
                </Box>
                {!isMobile && <CheckCircle sx={{ fontSize: 48, color: 'rgba(255,255,255,0.25)' }} />}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4} md={4}>
          <Card sx={{
            borderRadius: 3,
            background: 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)',
            boxShadow: '0 8px 32px rgba(234, 179, 8, 0.3)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(234, 179, 8, 0.4)' }
          }}>
            <CardContent sx={{ p: { xs: 1.5, md: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                    Εκκρεμή
                  </Typography>
                  <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ fontWeight: 700, color: '#fff', my: 0.5 }}>
                    {formatCurrency(generalStats.pending_amount)}
                  </Typography>
                </Box>
                {!isMobile && <HourglassEmpty sx={{ fontSize: 48, color: 'rgba(255,255,255,0.25)' }} />}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{
        p: { xs: 1.5, md: 2 }, mb: { xs: 2, md: 3 }, borderRadius: 3,
        border: '1px solid rgba(0,0,0,0.06)',
        background: 'rgba(249,115,22,0.02)'
      }}>
        <Grid container spacing={{ xs: 1, md: 2 }} alignItems="center">
          <Grid item xs={12} md={4}>
            <DateRangePickerField
              startDate={startDate}
              endDate={endDate}
              onRangeChange={(start, end) => {
                onStartDateChange(start);
                onEndDateChange(end);
              }}
              isMobile={isMobile}
              label="Περίοδος"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Κατηγορία</InputLabel>
              <Select value={filterCategory} onChange={(e) => onCategoryChange(e.target.value)} sx={{ borderRadius: 2 }}>
                <MenuItem value="">Όλες</MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Κατάσταση</InputLabel>
              <Select value={filterStatus} onChange={(e) => onStatusChange(e.target.value)} sx={{ borderRadius: 2 }}>
                <MenuItem value="">Όλα</MenuItem>
                <MenuItem value="pending">Εκκρεμή</MenuItem>
                <MenuItem value="partial">Μερικώς Πληρωμένα</MenuItem>
                <MenuItem value="paid">Πληρωμένα</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ mb: 2, display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
        <Button
          variant="contained"
          size={isMobile ? 'small' : 'medium'}
          startIcon={<Add />}
          fullWidth={isMobile}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          onClick={onAdd}
        >
          Νέο Έξοδο
        </Button>
      </Box>

      {/* Mobile: Card Layout */}
      {isMobile ? (
        <Stack spacing={1.5}>
          {generalExpenses.map((expense) => {
            const totalAmount = expense.total_amount || expense.amount || 0;
            const totalPaid = expense.total_paid || 0;
            const remainingAmount = totalAmount - totalPaid;

            return (
              <Card key={expense.id} variant="outlined">
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  {/* Row 1: Date + Category + Status */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {expense.expense_date}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {expense.category_name || 'Χωρίς κατηγορία'}
                      </Typography>
                    </Box>
                    {/* Status calculated from actual payments */}
                    {totalPaid >= totalAmount && totalAmount > 0 ? (
                      <Chip icon={<CheckCircle />} label="Πληρωμένο" color="success" size="small" />
                    ) : totalPaid > 0 ? (
                      <Chip icon={<Pending />} label={`-${formatCurrency(remainingAmount)}`} color="info" size="small" />
                    ) : (
                      <Chip icon={<Pending />} label="Εκκρεμές" color="warning" size="small" />
                    )}
                  </Box>

                  {/* Row 2: Description (if exists) */}
                  {expense.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
                      {expense.description}
                    </Typography>
                  )}

                  {/* Row 3: Amount Info */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Ποσό</Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {formatCurrency(totalAmount)}
                      </Typography>
                    </Box>
                    {totalPaid > 0 && (
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="text.secondary">Πληρωμένο</Typography>
                        <Typography variant="subtitle1" color="success.main" sx={{ fontWeight: 600 }}>
                          {formatCurrency(totalPaid)}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Row 4: Actions - Modern Outlined Icons */}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 1,
                    pt: 1.5,
                    borderTop: '1px solid',
                    borderColor: 'divider'
                  }}>
                    {(expense.payment_count > 0 || expense.total_paid > 0) && (
                      <IconButton
                        onClick={() => onOpenPaymentHistory(expense)}
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: '14px',
                          border: '2px solid #0ea5e9',
                          color: '#0ea5e9',
                          bgcolor: 'transparent',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: '#0ea5e9',
                            color: '#fff',
                            transform: 'scale(1.05)',
                          }
                        }}
                      >
                        <VisibilityOutlined sx={{ fontSize: 22 }} />
                      </IconButton>
                    )}
                    {expense.payment_status !== 'paid' && (
                      <>
                        <IconButton
                          onClick={() => onPayment(expense)}
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '14px',
                            border: '2px solid #3b82f6',
                            color: '#3b82f6',
                            bgcolor: 'transparent',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: '#3b82f6',
                              color: '#fff',
                              transform: 'scale(1.05)',
                            }
                          }}
                        >
                          <PaymentOutlined sx={{ fontSize: 22 }} />
                        </IconButton>
                      </>
                    )}
                    <IconButton
                      onClick={() => {
                        const formData = {
                          ...expense,
                          net_amount: expense.net_amount || (expense.total_amount / 1.24).toFixed(2),
                          vat_rate: expense.vat_rate || 24,
                          vat_amount: expense.vat_amount || (expense.total_amount - (expense.total_amount / 1.24)).toFixed(2),
                          total_amount: expense.total_amount || expense.amount
                        };
                        onEdit(formData);
                      }}
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '14px',
                        border: '2px solid #64748b',
                        color: '#64748b',
                        bgcolor: 'transparent',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: '#64748b',
                          color: '#fff',
                          transform: 'scale(1.05)',
                        }
                      }}
                    >
                      <EditOutlined sx={{ fontSize: 22 }} />
                    </IconButton>
                    <IconButton
                      onClick={() => onDelete(expense)}
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '14px',
                        border: '2px solid #ef4444',
                        color: '#ef4444',
                        bgcolor: 'transparent',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: '#ef4444',
                          color: '#fff',
                          transform: 'scale(1.05)',
                        }
                      }}
                    >
                      <DeleteOutlined sx={{ fontSize: 22 }} />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      ) : (
        /* Desktop: Table Layout */
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }}>Ημερομηνία</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }}>Κατηγορία</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }}>Ποσό</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }}>Πληρωμένο</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }}>Περιγραφή</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }}>Κατάσταση</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }} align="right">Ενέργειες</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {generalExpenses.map((expense) => {
                const totalAmount = expense.total_amount || expense.amount || 0;
                const totalPaid = expense.total_paid || 0;
                const remainingAmount = totalAmount - totalPaid;

                return (
                  <TableRow key={expense.id} sx={{ transition: 'background 0.15s', '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
                    <TableCell sx={{ fontWeight: 500 }}>{expense.expense_date}</TableCell>
                    <TableCell>
                      <Chip label={expense.category_name || 'Άλλο'} size="small" sx={{ fontWeight: 600, borderRadius: '8px', bgcolor: 'rgba(249,115,22,0.08)', color: '#ea580c' }} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{formatCurrency(totalAmount)}</TableCell>
                    <TableCell>
                      {totalPaid > 0 ? (
                        <Typography variant="body2" color="success.main">
                          {formatCurrency(totalPaid)}
                        </Typography>
                      ) : '-'}
                    </TableCell>
                    <TableCell>{expense.description || '-'}</TableCell>
                    <TableCell>
                      {/* Status calculated from actual payments */}
                      {totalPaid >= totalAmount && totalAmount > 0 ? (
                        <Chip
                          icon={<CheckCircle />}
                          label={`Πληρωμένο`}
                          color="success"
                          size="small"
                        />
                      ) : totalPaid > 0 ? (
                        <Chip
                          icon={<Pending />}
                          label={`Υπόλοιπο: ${formatCurrency(remainingAmount)}`}
                          color="info"
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={<Pending />}
                          label="Εκκρεμές"
                          color="warning"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {(expense.payment_count > 0 || expense.total_paid > 0) && (
                          <IconButton size="small" onClick={() => onOpenPaymentHistory(expense)} title="Ιστορικό Πληρωμών"
                            sx={{ bgcolor: 'rgba(14,165,233,0.08)', borderRadius: 2, '&:hover': { bgcolor: 'rgba(14,165,233,0.15)' } }}>
                            <Visibility sx={{ fontSize: 18, color: '#0ea5e9' }} />
                          </IconButton>
                        )}
                        {!(totalPaid >= totalAmount && totalAmount > 0) && (
                          <IconButton size="small" onClick={() => onPayment(expense)} title="Καταχώρηση Πληρωμής"
                            sx={{ bgcolor: 'rgba(59,130,246,0.08)', borderRadius: 2, '&:hover': { bgcolor: 'rgba(59,130,246,0.15)' } }}>
                            <Payment sx={{ fontSize: 18, color: '#3b82f6' }} />
                          </IconButton>
                        )}
                        <IconButton size="small" title="Επεξεργασία"
                          sx={{ bgcolor: 'rgba(100,116,139,0.08)', borderRadius: 2, '&:hover': { bgcolor: 'rgba(100,116,139,0.15)' } }}
                          onClick={() => {
                            const formData = {
                              ...expense,
                              net_amount: expense.net_amount || (expense.total_amount / 1.24).toFixed(2),
                              vat_rate: expense.vat_rate || 24,
                              vat_amount: expense.vat_amount || (expense.total_amount - (expense.total_amount / 1.24)).toFixed(2),
                              total_amount: expense.total_amount || expense.amount
                            };
                            onEdit(formData);
                          }}>
                          <Edit sx={{ fontSize: 18, color: '#64748b' }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => onDelete(expense)}
                          sx={{ bgcolor: 'rgba(239,68,68,0.08)', borderRadius: 2, '&:hover': { bgcolor: 'rgba(239,68,68,0.15)' } }}>
                          <Delete sx={{ fontSize: 18, color: '#ef4444' }} />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TablePagination
        component="div"
        count={generalExpensesPagination.total || 0}
        page={generalExpensePage}
        onPageChange={onPageChange}
        rowsPerPage={generalExpenseRowsPerPage}
        onRowsPerPageChange={(e) => {
          const val = parseInt(e.target.value, 10);
          onRowsPerPageChange(val);
        }}
        rowsPerPageOptions={[10, 50, 100]}
        labelRowsPerPage="Εμφάνιση ανά:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} από ${count !== -1 ? count : `περισσότερα από ${to}`}`}
      />
    </>
  );
}
